import React, { useRef } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { FormStepContentProps } from 'types';
import { FormDataType, useFormState, assistanceList } from 'contexts/FormProvider';
import { useScrollTop } from 'hooks/useScrollTop';
import { useCheckWindowCMS } from 'hooks/useCheckWindowCMS';
import validationSchema from './schema';
import { FormGroupLabel, TextField, Dropdown, Button } from '@qbe/ads-core';
import { FormCard } from '@qbe/ads-blocks-forms';
import { useContent, useContentContext } from '@qbe/dfc-content';
import FormSpacer from 'components/FormSpacer/FormSpacer';
import { setFormCompleteEvent, setFormStartEvent } from 'hooks/useAnalytics';
import { yupResolver } from '@hookform/resolvers/yup';

const TellUsMoreForm = ({ onComplete }: FormStepContentProps) => {
  useScrollTop();
  useCheckWindowCMS();

  const { formData } = useFormState();

  const form = useForm<FormDataType>({
    mode: 'onBlur',
    defaultValues: formData,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    resolver: yupResolver(validationSchema) as any,
  });

  const {
    handleSubmit,
    control,
    watch,
    register,
    formState: { errors },
  } = form;

  const { getTextContent } = useContent({
    context: useContentContext(),
  });

  // Container for info box
  const formRef = useRef<HTMLFormElement>(null);

  React.useEffect(() => {
    register('assistance');
  }, [register]);

  const isDisabled = !validationSchema.isValidSync(watch());

  const onSubmit = (newData: Partial<FormDataType>) => {
    setFormCompleteEvent();
    onComplete(newData);
  };

  // Localized content constants
  const pageHeading = getTextContent('Hardship.details.formHeading');
  const sectionHeading = getTextContent('Hardship.details.heading');
  const hardshipReasonTitle = getTextContent('Hardship.details.reason');
  const hardshipReasonPlaceholder = getTextContent('Hardship.details.reasonPlaceholder');
  const hardshipAssistanceTitle = getTextContent('Hardship.details.assistance');
  const hardshipAssistancePlaceholder = getTextContent('Hardship.details.assistancePlaceholder');
  const hardshipDescriptionTitle = getTextContent('Hardship.details.description');
  const hardshipDescriptionPlaceholder = getTextContent('Hardship.details.descriptionPlaceholder');
  const submitLabel = getTextContent('Hardship.details.submit.label');

  return (
    <form onSubmit={handleSubmit(onSubmit)} ref={formRef} data-test="hardship-form">
      <FormCard variant="pageTitle" title={pageHeading} description="" />

      <FormSpacer type="pagesection" />

      <FormCard variant="sectionTitle" title={sectionHeading}>
        <FormGroupLabel title={hardshipReasonTitle} required alignCenter />
        <FormSpacer type="input" />
        <Controller
          name="reason"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              label={hardshipReasonPlaceholder}
              id="reason"
              value={field.value as string}
              multiline
              maxLength={4000}
              error={!!errors.reason}
              helperText={errors.reason && String(errors.reason?.message)}
              data-test="reason"
              data-track="reason"
              data-track-value="mask"
              onChange={(e) => {
                const value = e.target.value;
                field.onChange(value);
                setFormStartEvent();
              }}
            />
          )}
        />

        <FormSpacer type="input" />

        <FormGroupLabel title={hardshipAssistanceTitle} required alignCenter />
        <FormSpacer type="input" />
        <Controller
          name="assistance"
          control={control}
          render={({ field }) => (
            <Dropdown
              {...field}
              id="assistance"
              multiple={false}
              value={field.value as string}
              label={hardshipAssistancePlaceholder}
              error={!!errors.assistance}
              optionList={assistanceList.map((i) => ({ label: i, value: i }))}
              data-test="assistance"
              data-track="assistance"
              data-track-value="capture"
            />
          )}
        />

        <FormSpacer type="input" />

        <FormGroupLabel title={hardshipDescriptionTitle} alignCenter />
        <FormSpacer type="input" />
        <Controller
          name="description"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              label={hardshipDescriptionPlaceholder}
              id="description"
              multiline
              value={field.value as string}
              maxLength={4000}
              data-test="description"
              data-track="description"
              data-track-value="mask"
              error={!!errors.description}
              helperText={errors.description && String(errors.description?.message)}
            />
          )}
        />

        <FormSpacer type="input" />

        <Button label={submitLabel} type="submit" disabled={isDisabled} />
      </FormCard>
    </form>
  );
};

export default TellUsMoreForm;
